import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

const PERIODS = [
  { id: 'day', label: 'Daily' },
  { id: 'week', label: 'Weekly' },
  { id: 'month', label: 'Monthly' },
];

export default function StatsTab() {
  const { user } = useAuth();
  const isOwner = user?.role === 'admin';
  const [period, setPeriod] = useState('day');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/generation/stats`, {
        headers: authHeaders(),
        params: { period },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [period]);

  return (
    <div className="space-y-6">
      {/* Header cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-zinc-500">Total generations</div>
            <div className="text-3xl font-bold text-white mt-1" data-testid="stats-total-count">
              {data?.total_count ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-zinc-500">Total cost (USD)</div>
            <div className="text-3xl font-bold text-white mt-1" data-testid="stats-total-cost">
              ${(data?.total_cost_usd ?? 0).toFixed(3)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-5 space-y-2">
            <div className="text-xs uppercase tracking-wider text-zinc-500">Period</div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="stats-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {/* Buckets table */}
      <Card className="bg-zinc-950 border-zinc-800">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-white mb-3">
            Cost by {period === 'day' ? 'day' : period === 'week' ? 'week' : 'month'}
          </h3>
          {loading ? (
            <div className="text-sm text-zinc-500">Loading...</div>
          ) : (data?.buckets || []).length === 0 ? (
            <div className="text-sm text-zinc-500">No data for this period.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-zinc-500 border-b border-zinc-800">
                    <th className="py-2 pr-4">Period</th>
                    <th className="py-2 pr-4">Count</th>
                    <th className="py-2 pr-4">Cost (USD)</th>
                    <th className="py-2">By model</th>
                  </tr>
                </thead>
                <tbody>
                  {data.buckets.map((b) => (
                    <tr key={b.period} className="border-b border-zinc-900" data-testid={`stats-bucket-${b.period}`}>
                      <td className="py-2 pr-4 text-white font-mono">{b.period}</td>
                      <td className="py-2 pr-4 text-zinc-300">{b.count}</td>
                      <td className="py-2 pr-4 text-white font-semibold">${b.cost_usd.toFixed(3)}</td>
                      <td className="py-2 text-xs text-zinc-400">
                        {Object.entries(b.by_model || {}).map(([m, c]) => (
                          <span key={m} className="inline-block mr-3">
                            {m}: <span className="text-zinc-200">${c.toFixed(3)}</span>
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Owner-only per-user breakdown */}
      {isOwner && (data?.by_va || []).length > 0 && (
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Cost by user</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-zinc-500 border-b border-zinc-800">
                    <th className="py-2 pr-4">User</th>
                    <th className="py-2 pr-4">Role</th>
                    <th className="py-2 pr-4">Count</th>
                    <th className="py-2">Cost (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_va.map((v) => (
                    <tr key={v.user_email} className="border-b border-zinc-900" data-testid={`stats-user-${v.user_email}`}>
                      <td className="py-2 pr-4">
                        <div className="text-white">{v.user_name}</div>
                        <div className="text-xs text-zinc-500">{v.user_email}</div>
                      </td>
                      <td className="py-2 pr-4 text-zinc-300">{v.va_role || '—'}</td>
                      <td className="py-2 pr-4 text-zinc-300">{v.count}</td>
                      <td className="py-2 text-white font-semibold">${v.cost_usd.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
